import { useLayoutEffect, useRef, useState } from 'react';

/**
 * 面板宽度的**入场动画**：新打开的那一页从 `0` 长到目标宽度（配合 `.ws-pane`
 * 上的 CSS `transition: width`）。
 *
 * 为什么需要它：面板宽度从 `0` 过渡到目标宽度。面板是窗口内**最右侧**的元素，
 * 于是看上去是**从窗体右侧向左伸出**，而不是"啪"地占掉一块。
 * （**窗口不会跟着变宽** —— 那套机制已按用户要求移除，见 CHANGELOG。）
 *
 * 另一条来由：四页必须**共用同一条路径**。之前只有 AI 助手（常驻挂载，宽度 0↔W 走
 * CSS 过渡）有动画，另外三页是挂载/卸载直接就位 —— 用户看到的就是"AI 会有收起再打开的
 * 动作，而另外三个页之间切换没有变化"。
 *
 * 拖动分隔条改变宽度时**不重放动画**（直接跟随），否则拖起来会发飘。
 *
 * ## ⚠️ 为什么必须是**两次** requestAnimationFrame
 *
 * 用户实测过一个只在"每个作品里第一次打开某一页"出现的毛病：**面板瞬间出现**（没有伸出动作），
 * 而之后无论怎么反复开关都丝滑。原因就在这里：
 *
 * - 那一页的 `section` 是**第一次挂载**（`{aiMounted && <section>}`），刚插入 DOM、
 *   还没有 computed style；
 * - 单次 rAF 的回调**仍可能跑在"宽度 0"那一帧真正绘制之前**，于是浏览器只看到
 *   "0 → 目标宽度"一次变化 —— 过渡无从发生，直接到位；
 * - 之后再开关时，`section` 早已绘制在宽度 0 上，单次 rAF 就足够。
 *
 * 两次 rAF 保证中间**至少有一帧被真正绘制**（`0` 那一帧），过渡因此一定有起点。
 * 这条不能"优化"回一次 —— 改回单次就会重现"首次打开瞬间出现"。
 */
export function usePaneEnterWidth(paneKey: string | null, targetWidth: number): number {
  const [width, setWidth] = useState(0);
  const lastKey = useRef<string | null>(null);
  const rafs = useRef<number[]>([]);

  useLayoutEffect(() => {
    const cancelPending = () => {
      rafs.current.forEach(id => cancelAnimationFrame(id));
      rafs.current = [];
    };
    const switched = lastKey.current !== paneKey;
    lastKey.current = paneKey;

    if (!paneKey) { cancelPending(); setWidth(0); return; }
    // 同一页只是宽度变了（拖动分隔条）：直接跟随，不重放动画
    if (!switched) { cancelPending(); setWidth(targetWidth); return; }

    // 换了页（含"这一页第一次打开"= section 首次挂载）：先落到 0，等真正画过一帧再给目标值
    cancelPending();
    setWidth(0);
    rafs.current.push(
      requestAnimationFrame(() => {
        rafs.current.push(requestAnimationFrame(() => setWidth(targetWidth)));
      }),
    );
    return cancelPending;
  }, [paneKey, targetWidth]);

  return width;
}
